import { BaseProvider as BaseWebThemeProvider } from 'baseui';
import { Theme as BwTheme } from 'baseui/theme';
import Head from 'next/head';
import { useMemo } from 'react';
import {
  createGlobalStyle,
  ThemeProvider as StyledComponentsThemeProvider,
} from 'styled-components';
import { Provider as StyletronProvider } from 'styletron-react';
import { buildTheme } from '../../../UnicronApp/buildTheme';
import { ZIndex } from '../../styles/constants';
import { rehydrateClientEngine } from '../../styles/styletron/client';
import { ChildrenOnly } from '../../types/ChildrenOnly';
import { useObservable } from '../../util/Observable';
import { useServices } from '../Services';
import { HeThemeParameters } from './HeThemeParameters.type';

/** Takes theme parameters from the ThemeService, builds a full theme from them
 * and makes it available to styled-components and baseweb.
 */
export const HeThemeProvider = ({ children }: ChildrenOnly) => {
  const services = useServices();
  const themeService = services.theme;
  const screenSize = useObservable(services.screenSize.observable);
  const params = themeService.getParams();

  /** On the server, there will be no client styletron engine to supply.
   * In that case, we assume we are being server-rendered and that a server styletron engine
   * will be supplied by an ancestor.
   */
  const clientStyletronEngine = useMemo(() => rehydrateClientEngine(), []);

  const baseWebTheme = useMemo(() => buildTheme(params), [params]);

  const faviconUrl = params.faviconUrl;

  const output = (
    <>
      {faviconUrl && (
        <Head>
          <link href={faviconUrl} rel="icon" key="favicon" />
        </Head>
      )}
      <StyledComponentsThemeProvider
        theme={parentTheme => ({
          ...parentTheme,
          whiteLabelProps: params,
          screenSize,
          ...baseWebTheme,
        })}
      >
        <BwCompatibilityHacks />

        <BaseWebThemeProvider theme={baseWebTheme}>{children}</BaseWebThemeProvider>
      </StyledComponentsThemeProvider>
    </>
  );

  /** When no client engine exists, assume that a server engine will be supplied by an ancestor */
  if (clientStyletronEngine) {
    return <StyletronProvider value={clientStyletronEngine}>{output}</StyletronProvider>;
  }

  return output;
};

/** Tell styled-components we will be putting our theme properties onto its context object */
declare module 'styled-components' {
  export interface DefaultTheme {
    whiteLabelProps?: HeThemeParameters;

    /** For convenience, we unroll the baseweb theme onto the styled-components theme */
    name: BwTheme['name'];
    direction: BwTheme['direction'];
    breakpoints: BwTheme['breakpoints'];
    mediaQuery: BwTheme['mediaQuery'];
    grid: BwTheme['grid'];
    colors: BwTheme['colors'];
    typography: BwTheme['typography'];
    sizing: BwTheme['sizing'];
    lighting: BwTheme['lighting'];
    animation: BwTheme['animation'];
    borders: BwTheme['borders'];
    zIndex: BwTheme['zIndex'];
  }
}

/** This hack is required to get baseweb components to appear above other components which
 * already use z-index, eg ReactModal. Can be removed once modals and layers are all using
 * Baseweb's own components.
 */
const BwCompatibilityHacks = createGlobalStyle`
  div[data-baseweb="popover"], div[data-baseweb="toaster"], div[data-baseweb="modal"] {
    z-index: ${ZIndex.BASEWEB};
  }
`;
